// Locale-aware formatting for the public invoice templates.
//
// Every string an invoice template prints for money, quantities, percentages
// and dates comes from here, so all ten templates agree and nothing formats
// with a bare `toFixed(2)` again (`$1250.00` → `$1,250.00`).
//
// Two rules hold throughout:
//
// - Nothing throws. Values arrive from Firestore through a public payload and
//   may be strings, nulls or garbage; a bad value degrades to a neutral
//   rendering (0, the raw text, or null), never to an exception mid-render.
// - Dates are calendar dates, not instants. The app stores `MM/dd/yyyy` with
//   no zone, so a parsed date is pinned to 12:00 UTC and formatted in UTC. The
//   same invoice therefore prints the same day on the server, in every
//   visitor's browser and in tests.

export const DEFAULT_INVOICE_LOCALE = 'en-US'

const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur'])

/**
 * An app locale id (`en_US`, `pl`, `pt_BR`, `is_IS`) → its canonical BCP-47
 * tag (`en-US`, `pl`, `pt-BR`, `is-IS`), whether or not this engine's Intl
 * can format in it — the language a document is written in, for `lang`.
 *
 * POSIX decorations (`.UTF-8`, `@latin`) and Unicode extensions (`-u-nu-arab`)
 * are dropped; deprecated codes are canonicalised (`iw` → `he`, `tl` →
 * `fil`); anything malformed is null.
 */
export function canonicalLocaleTag(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const tag = raw.trim().replace(/[.@].*$/, '').replace(/_/g, '-')
  if (!tag || tag.length > 64 || !/^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/.test(tag)) return null
  try {
    const canonical = Intl.getCanonicalLocales(tag)[0]
    if (!canonical) return null
    return typeof Intl.Locale === 'function' ? new Intl.Locale(canonical).baseName : canonical
  }
  catch {
    return null
  }
}

/**
 * The workspace's `info.document_locale` (an app locale id such as `en_US`,
 * `pl` or `pt_BR`) → a BCP-47 tag Intl supports, e.g. `en-US` — the locale
 * money, quantities and dates are formatted in.
 *
 * Decorations and extensions are dropped as canonicalLocaleTag() drops them;
 * anything malformed or unsupported becomes `en-US`.
 */
export function localeFromDocumentLocale(raw: unknown): string {
  const tag = canonicalLocaleTag(raw)
  if (!tag) return DEFAULT_INVOICE_LOCALE
  try {
    return Intl.NumberFormat.supportedLocalesOf([tag])[0] ?? DEFAULT_INVOICE_LOCALE
  }
  catch {
    return DEFAULT_INVOICE_LOCALE
  }
}

/** True for Arabic, Hebrew, Persian and Urdu (`ar`, `he`, `fa`, `ur`, any region). */
export function isRtlLocale(locale: unknown): boolean {
  if (typeof locale !== 'string') return false
  const language = locale.trim().split(/[-_]/)[0]?.toLowerCase() ?? ''
  return RTL_LANGUAGES.has(language)
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

const NUMERIC_STRING = /^[-+]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[-+]?\d+)?$/i

/**
 * Coerces a stored amount to a finite number. Numbers pass through; numeric
 * strings (`"100"`, `" 12.50 "`) are parsed; everything else — NaN, ±Infinity,
 * `"12abc"`, `"0x10"`, `"1,234.50"`, null, objects — is 0.
 */
export function toAmount(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.length === 0 || trimmed.length > 64 || !NUMERIC_STRING.test(trimmed)) return 0
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

/**
 * Like toAmount, but tells "no value" apart from zero: null for absent, empty
 * or non-numeric input.
 */
export function toOptionalAmount(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.length === 0 || trimmed.length > 64 || !NUMERIC_STRING.test(trimmed)) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const numberFormatCache = new Map<string, Intl.NumberFormat>()

/** Cached Intl.NumberFormat; an unusable locale falls back to en-US. */
function numberFormat(locale: string, options: Intl.NumberFormatOptions, key: string): Intl.NumberFormat {
  const cacheKey = `${locale}|${key}`
  const cached = numberFormatCache.get(cacheKey)
  if (cached) return cached
  let formatter: Intl.NumberFormat
  try {
    formatter = new Intl.NumberFormat(locale, options)
  }
  catch {
    formatter = new Intl.NumberFormat(DEFAULT_INVOICE_LOCALE, options)
  }
  numberFormatCache.set(cacheKey, formatter)
  return formatter
}

/** Rounds away a value that would print as a signed zero ("-$0.00"). */
function withoutNegativeZero(value: number, fractionDigits: number): number {
  const factor = 10 ** fractionDigits
  return Math.round(Math.abs(value) * factor) === 0 ? 0 : value
}

let knownCurrencies: Set<string> | null | undefined

function isKnownCurrency(code: string): boolean {
  if (knownCurrencies === undefined) {
    try {
      knownCurrencies = typeof Intl.supportedValuesOf === 'function'
        ? new Set(Intl.supportedValuesOf('currency'))
        : null
    }
    catch {
      knownCurrencies = null
    }
  }
  // Without the list (older engines) every well-formed code is attempted.
  return knownCurrencies === null || knownCurrencies.has(code)
}

/** Upper-cased ISO 4217 code, or '' when the value is not a well-formed, known code. */
export function normalizeCurrencyCode(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  const code = raw.trim().toUpperCase()
  return /^[A-Z]{3}$/.test(code) && isKnownCurrency(code) ? code : ''
}

/** The locale's symbol for a currency (`EUR` → `€` in en-US), or '' when the code is unusable. */
export function currencySymbolFor(code: unknown, locale: string = DEFAULT_INVOICE_LOCALE): string {
  const currency = normalizeCurrencyCode(code)
  if (!currency) return ''
  try {
    const parts = numberFormat(locale, { style: 'currency', currency }, `currency:${currency}`).formatToParts(0)
    return parts.find(part => part.type === 'currency')?.value ?? ''
  }
  catch {
    return ''
  }
}

export interface MoneyFormatOptions {
  /** ISO 4217 code, e.g. "USD". Invalid or unknown codes fall back to the symbol. */
  currencyCode?: string | null
  /** Used only when the code cannot be formatted by Intl. */
  currencySymbol?: string | null
  /** BCP-47 locale; defaults to en-US. */
  locale?: string | null
}

/**
 * Formats an amount as currency: `formatMoney(1250, { currencyCode: 'USD' })`
 * → `$1,250.00`; with `locale: 'pl'` and `PLN` → `1250,00 zł`.
 *
 * Accepts numbers or numeric strings (anything else is 0). When the currency
 * code is missing, malformed or unknown to Intl, prints the stored symbol and
 * a grouped two-decimal number instead (`-$5.00`, sign before the symbol).
 */
export function formatMoney(amount: unknown, options: MoneyFormatOptions = {}): string {
  const locale = options.locale || DEFAULT_INVOICE_LOCALE
  const value = toAmount(amount)
  const code = normalizeCurrencyCode(options.currencyCode)
  if (code) {
    try {
      const formatter = numberFormat(locale, { style: 'currency', currency: code }, `currency:${code}`)
      const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2
      return formatter.format(withoutNegativeZero(value, digits))
    }
    catch {
      // Fall through to the symbol rendering below.
    }
  }
  const symbol = typeof options.currencySymbol === 'string' ? options.currencySymbol.trim() : ''
  const rawCode = typeof options.currencyCode === 'string' ? options.currencyCode.trim().slice(0, 8) : ''
  const prefix = symbol || (rawCode ? `${rawCode} ` : '')
  const safe = withoutNegativeZero(value, 2)
  const grouped = numberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, 'money-fallback')
    .format(Math.abs(safe))
  return `${safe < 0 ? '-' : ''}${prefix}${grouped}`
}

/**
 * An amount as a banking app's amount field takes it: no symbol, no
 * grouping, a dot, and exactly the currency's minor digits —
 * `plainAmount(7102.28, 'USD')` → `7102.28`, `plainAmount(1500, 'JPY')` →
 * `1500`, `plainAmount(1.5, 'KWD')` → `1.500`. Unknown codes use 2 digits.
 * What the page's copy button puts on the clipboard for "Amount due".
 */
export function plainAmount(amount: unknown, currencyCode?: string | null): string {
  const code = normalizeCurrencyCode(currencyCode)
  let digits = 2
  if (code) {
    try {
      digits = numberFormat(DEFAULT_INVOICE_LOCALE, { style: 'currency', currency: code }, `currency:${code}`)
        .resolvedOptions().maximumFractionDigits ?? 2
    }
    catch {
      digits = 2
    }
  }
  const value = withoutNegativeZero(toAmount(amount), digits)
  return value.toFixed(digits)
}

/** A quantity with at most two decimals and no trailing zeros: 1 → "1", 1.5 → "1.5", 2.25 → "2.25". */
export function formatQuantity(value: unknown, locale: string = DEFAULT_INVOICE_LOCALE): string {
  const quantity = withoutNegativeZero(toAmount(value), 2)
  return numberFormat(locale, { maximumFractionDigits: 2 }, 'quantity').format(quantity)
}

/** A rate given in percent points: 8.25 → "8.25%", 7 → "7%" (locale-aware, at most three decimals). */
export function formatPercent(value: unknown, locale: string = DEFAULT_INVOICE_LOCALE): string {
  const percent = withoutNegativeZero(toAmount(value), 3)
  return numberFormat(locale, { style: 'percent', maximumFractionDigits: 3 }, 'percent').format(percent / 100)
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?=$|[T ])/
const EPOCH_DIGITS = /^\d{1,16}$/
/** The largest instant a JS Date can hold. */
const MAX_EPOCH_MS = 8.64e15
const MAX_RAW_DATE_LENGTH = 100

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/** 12:00 UTC on a calendar day, or null when the day does not exist. */
function utcNoon(year: number, month: number, day: number): Date | null {
  if (year < 1000 || year > 9999 || month < 1 || month > 12) return null
  if (day < 1 || day > daysInMonth(year, month)) return null
  return new Date(Date.UTC(year, month - 1, day, 12))
}

/** The UTC calendar day of an epoch-ms instant, pinned to 12:00 UTC. */
function utcNoonOfInstant(ms: number): Date | null {
  if (!Number.isFinite(ms) || ms <= 0 || ms > MAX_EPOCH_MS) return null
  const instant = new Date(ms)
  return utcNoon(instant.getUTCFullYear(), instant.getUTCMonth() + 1, instant.getUTCDate())
}

/** Epoch ms from a number, an all-digit string or a serialised Firestore Timestamp. */
function epochMs(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return EPOCH_DIGITS.test(trimmed) ? Number(trimmed) : null
  }
  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>
    const seconds = typeof record.seconds === 'number' ? record.seconds : record._seconds
    if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return null
    const nanos = typeof record.nanoseconds === 'number' ? record.nanoseconds : record._nanoseconds
    return seconds * 1000 + (typeof nanos === 'number' && Number.isFinite(nanos) ? Math.floor(nanos / 1e6) : 0)
  }
  return null
}

/**
 * Parses a stored invoice date into a Date at 12:00 UTC on that calendar day.
 *
 * Accepts the app's `MM/dd/yyyy` (also `M/d/yyyy`), `yyyy-MM-dd`, ISO
 * date-times (the calendar date as written is kept, whatever the offset),
 * epoch milliseconds as a number or an all-digit string, a serialised
 * Firestore Timestamp and a valid Date. Anything else — including impossible
 * days like 02/30/2026 and non-positive epochs — is null.
 */
export function parseInvoiceDate(value: unknown): Date | null {
  if (value instanceof Date) {
    const ms = value.getTime()
    return Number.isNaN(ms) ? null : utcNoon(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate())
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed.length > 64) return null
    const us = US_DATE.exec(trimmed)
    if (us) return utcNoon(Number(us[3]), Number(us[1]), Number(us[2]))
    const iso = ISO_DATE.exec(trimmed)
    if (iso) {
      if (trimmed.length > 10 && Number.isNaN(Date.parse(trimmed))) return null
      return utcNoon(Number(iso[1]), Number(iso[2]), Number(iso[3]))
    }
    if (EPOCH_DIGITS.test(trimmed)) return utcNoonOfInstant(Number(trimmed))
    return null
  }
  const ms = epochMs(value)
  return ms === null ? null : utcNoonOfInstant(ms)
}

const dateFormatCache = new Map<string, Intl.DateTimeFormat>()

function mediumDate(date: Date, locale: string): string {
  let formatter = dateFormatCache.get(locale)
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' })
    }
    catch {
      formatter = new Intl.DateTimeFormat(DEFAULT_INVOICE_LOCALE, { dateStyle: 'medium', timeZone: 'UTC' })
    }
    dateFormatCache.set(locale, formatter)
  }
  return formatter.format(date)
}

/**
 * A stored invoice date as a medium date in the document locale
 * (`07/14/2026` → `Jul 14, 2026` in en-US, `14 lip 2026` in pl).
 *
 * null when the value is empty (null, '', whitespace, a non-positive epoch).
 * A non-empty string that does not parse is shown as written (trimmed, capped
 * at 100 characters) rather than dropped — the owner typed it on purpose.
 */
export function formatInvoiceDate(value: unknown, locale: string = DEFAULT_INVOICE_LOCALE): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' && value.trim().length === 0) return null
  const parsed = parseInvoiceDate(value)
  if (parsed) return mediumDate(parsed, locale)
  return typeof value === 'string' ? value.trim().slice(0, MAX_RAW_DATE_LENGTH) : null
}

/**
 * An epoch-ms instant (number, all-digit string or serialised Firestore
 * Timestamp) as a medium date of its UTC calendar day; null when absent,
 * non-positive or not an epoch.
 */
export function formatEpochDate(value: unknown, locale: string = DEFAULT_INVOICE_LOCALE): string | null {
  const ms = epochMs(value)
  if (ms === null) return null
  const date = utcNoonOfInstant(ms)
  return date ? mediumDate(date, locale) : null
}

const instantFormatCache = new Map<string, Intl.DateTimeFormat>()

function instantFormat(locale: string, timeZone: string | undefined): Intl.DateTimeFormat {
  const key = `${locale}|${timeZone ?? ''}`
  const cached = instantFormatCache.get(key)
  if (cached) return cached
  let formatter: Intl.DateTimeFormat
  try {
    formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', ...(timeZone ? { timeZone } : {}) })
  }
  catch {
    // An unusable locale or zone: en-US in the viewer's zone.
    formatter = new Intl.DateTimeFormat(DEFAULT_INVOICE_LOCALE, { dateStyle: 'medium' })
  }
  instantFormatCache.set(key, formatter)
  return formatter
}

/**
 * A REAL instant — an installment's `paid_at`, an invoice's `signed_at` — as
 * a medium date of the day it happened where the reader is: in `timeZone`
 * when given, else in the viewer's own zone. (Calendar dates such as a due
 * date go through formatInvoiceDate instead, pinned to their UTC day.) A
 * payment made at 7 PM in Los Angeles on Jul 14 prints "Jul 14", not the
 * "Jul 15" of its UTC day. null when absent, non-positive or not an epoch.
 */
export function formatInstantDate(value: unknown, locale: string = DEFAULT_INVOICE_LOCALE, timeZone?: string): string | null {
  const ms = epochMs(value)
  if (ms === null || !Number.isFinite(ms) || ms <= 0 || ms > MAX_EPOCH_MS) return null
  return instantFormat(locale, timeZone).format(new Date(ms))
}
